import dayjs from "dayjs";

import { formatCurrency } from "@/helpers/currency-helpers";
import { formatCpfOrCnpj } from "@/helpers/document-helpers";
import {
  groupOrderPaymentsForDisplay,
  GroupedOrderPaymentDisplay,
} from "@/helpers/order-payment-display.helpers";
import { formatPhoneNumber } from "@/helpers/phone-helpers";
import { IOrderInput, IOrderItem, IOrderPayment } from "@/interfaces/order.interfaces";
import { OrdersStatusMessages } from "@/messages/orders/order-status.messages";
import { OrderStatus } from "@/types/order.types";

type UseOrderDataReturn = Partial<IOrderInput> & {
  formattedOrderDate: string;
  formattedOrderAmount: string;
  formattedOrderFreight: string;
  formattedOrderDiscount: string;
  formattedOrderCharges: string;
  formattedOrderFee: string;
  formattedTotalAmount: string;
  formattedBillingAddress: string;
  formattedItems: Array<{
    skuCode: string;
    description: string;
    quantity: number;
    formattedGrossUnitPrice: string;
    formattedNetLineAmount: string;
    userComment?: string | null;
  }>;
  formattedPayments: Array<{
    id: string;
    dueDate: string;
    paymentDate: string;
    paymentAmount: string;
    paymentMethodType: string;
    paymentMethodName: string;
  }>;
  groupedPaymentSummaries: GroupedOrderPaymentDisplay[];
  formattedOrderStatus: string;
  formattedShippingDocument: string;
  hasItemsWithComments: boolean;
};

type UseOrderDataProps = { order: IOrderInput | null; messages?: OrdersStatusMessages };

function useOrderData({ order, messages }: UseOrderDataProps): UseOrderDataReturn {
  const formattedOrderDate = order?.orderDate
    ? dayjs(order.orderDate).format("DD/MM/YYYY HH:mm")
    : "-";

  const formattedOrderAmount = formatCurrency(order?.orderAmount || 0, "pt-BR", "BRL");
  const formattedOrderFreight = formatCurrency(order?.orderFreight || 0, "pt-BR", "BRL");
  const formattedOrderDiscount = formatCurrency(order?.orderDiscount || 0, "pt-BR", "BRL");
  const formattedOrderCharges = formatCurrency(order?.orderCharges || 0, "pt-BR", "BRL");
  const formattedOrderFee = formatCurrency(order?.orderFee || 0, "pt-BR", "BRL");
  const formattedTotalAmount = formatCurrency(order?.totalAmount || 0, "pt-BR", "BRL");

  const orderNumberAndDate =
    order && order.orderNumber && order.orderDate
      ? `${order.orderNumber} de ${dayjs(order.orderDate).format("DD.MM.YYYY")}`
      : "-";

  const resolvePaymentDisplayAmount = (payment: IOrderPayment) => {
    if (payment.value != null && payment.value > 0) {
      return payment.value;
    }

    const discount = payment.discountPerPaymentType ?? 0;
    const chargedAmount = Number(payment.paymentAmount) - discount;

    return chargedAmount > 0 ? chargedAmount : Number(payment.paymentAmount);
  };

  const formattedPayments =
    order?.payments?.map((payment: IOrderPayment) => {
      return {
        id: payment.id ? String(payment.id) : "",
        dueDate: payment.dueDate ? dayjs(payment.dueDate).format("DD/MM/YYYY") : "-",
        paymentDate: payment.paymentDate ? dayjs(payment.paymentDate).format("DD/MM/YYYY") : "-",
        paymentAmount: formatCurrency(resolvePaymentDisplayAmount(payment), "pt-BR", "BRL"),
        paymentMethodType: payment.paymentMethodType,
        paymentMethodName: payment.paymentMethodName,
      };
    }) || [];

  const groupedPaymentSummaries = groupOrderPaymentsForDisplay(order?.payments);

  const formattedItems =
    order?.items?.map((item: IOrderItem) => {
      return {
        skuCode: item.skuCode,
        description: item.description,
        quantity: item.quantity,
        formattedGrossUnitPrice: formatCurrency(Number(item.grossUnitPrice) || 0, "pt-BR", "BRL"),
        formattedNetLineAmount: formatCurrency(Number(item.netLineAmount) || 0, "pt-BR", "BRL"),
        userComment: item.userComment || null,
      };
    }) || [];

  const hasItemsWithComments = formattedItems.some((item) => item.userComment);

  const formattedShippingAddress = order
    ? [
        order.shippingAddress,
        order.shippingAddressNumber,
        order.shippingAddressCompl,
        order.shippingAddressDistrict,
        order.shippingAddressCity,
        order.shippingAddressState,
        order.shippingAddressCountry,
        order.shippingAddressZipcode ? `CEP: ${order.shippingAddressZipcode}` : null,
      ]
        .filter(Boolean)
        .join(", ")
    : "-";

  const formattedShippingPhone = order?.shippingPhoneNumber
    ? formatPhoneNumber(order.shippingPhoneNumber || "")
    : "-";

  const formattedShippingCellphone = order?.shippingPhoneNumber
    ? formatPhoneNumber(order.shippingPhoneNumber || "")
    : "-";

  const formattedBillingAddress = order
    ? [order.billingAddress, order.billingAddressNumber, order.billingAddressCompl]
        .filter(Boolean)
        .join(", ")
    : "-";

  const formattedBillingPhone = order?.billingPhoneNumber
    ? formatPhoneNumber(order.billingPhoneNumber || "")
    : "-";
  const formattedBillingCellphone = order?.billingPhoneNumber
    ? formatPhoneNumber(order.billingPhoneNumber || "")
    : "-";

  const formattedShippingDocument = order?.shippingDocument
    ? formatCpfOrCnpj(order.shippingDocument)
    : "-";

  const getOrderStatusMessage = (orderStatus: string): string => {
    if (!messages) return orderStatus;

    switch (orderStatus) {
      case OrderStatus.Confirmed:
        return messages.confirmedOption;
      case OrderStatus.Paid:
        return messages.paidOption;
      case OrderStatus.Handling:
        return messages.handlingOption;
      case OrderStatus.Invoiced:
        return messages.invoicedOption;
      case OrderStatus.Shipped:
        return messages.shippedOption;
      case OrderStatus.Delivered:
        return messages.deliveredOption;
      case OrderStatus.Waiting:
        return messages.waitingOption;
      case OrderStatus.Cancelled:
        return messages.cancelledOption;
      case OrderStatus.Error:
        return messages.errorOption;
      case OrderStatus.CancellationRequest:
        return messages.cancellationRequestOption;

      default:
        return messages.statusUnknown;
    }
  };

  const formattedOrderStatus = order?.orderStatus
    ? getOrderStatusMessage(order.orderStatus)
    : "-";

  return {
    ...order,
    formattedOrderDate,
    formattedOrderAmount,
    formattedOrderFreight,
    formattedOrderDiscount,
    formattedOrderCharges,
    formattedOrderFee,
    formattedPayments,
    groupedPaymentSummaries,
    formattedItems,
    hasItemsWithComments,
    orderNumberAndDate,
    formattedShippingAddress,
    formattedShippingPhone,
    formattedShippingCellphone,
    formattedBillingAddress,
    formattedBillingPhone,
    formattedBillingCellphone,
    formattedOrderStatus,
    formattedShippingDocument,
    formattedTotalAmount,
  };
}

export default useOrderData;
